import { IconButton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "../../../../assets/images/download-proposal.svg";
import { Proposal, ProposalLayout, useDownloadProposalMutation } from "generated/graphql";
import useCustomGetProposal from "hooks/proposals/useCustomGetProposal";
import { useState, useCallback } from "react";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import useConfirmationDialog from "hooks/useConfirmationDialog";

export default function DownloadProposal({ proposal }: { proposal: Proposal }) {
  const [downloadProposal] = useDownloadProposalMutation();
  const { show, close } = useGlobalMessage();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [toastKey, setToastKey] = useState<string | number | null>(null);
  const { refetch } = useCustomGetProposal({ id: proposal.id });
  const [error, setError] = useState<null | string>(null);

  const validStatuses = [
    "SENT",
    "DRAFT",
    "CANCELED",
    "CLOSED_LOST",
    "CLOSED_WON",
    "OPEN",
    "CHANGE_ORDER",
    "NEW_VERSION",
    "ON_HOLD",
  ];
  const isDisabled = !validStatuses.includes(proposal.status) || isDownloading || isPolling;

  // Check if PDF is available and up to date
  const isPdfReady = useCallback(() => {
    if (!proposal.proposalPdf || !proposal.pdfGeneratedAt) return false;

    const updatedAt = new Date(proposal.updatedAt);
    const pdfGeneratedAt = new Date(proposal.pdfGeneratedAt);

    return pdfGeneratedAt >= updatedAt;
  }, [proposal.proposalPdf, proposal.pdfGeneratedAt, proposal.updatedAt]);

  // Download the PDF file
  const downloadPdfFile = useCallback(
    (url: string) => {
      const link = document.createElement("a");
      link.href = `${import.meta.env.VITE_CDN_BASE_URL}/${url}`;
      link.download = `proposal-${proposal.id}.pdf`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [proposal.id]
  );

  const handleJobSizeError = (result) => {
    // @ts-ignore
    if (result?.message?.includes("Please input a job size.")) {
      // @ts-ignore
      setError(result.message);
      setIsDownloading(false);
      setOpen(true);
    } else {
      if (toastKey) {
        close(toastKey);
        setToastKey(null);
      }
      show({
        message: "PDF generation failed. Please try again.",
        options: { variant: "error" },
      });
      setIsDownloading(false);
    }
  };

  // Poll for PDF generation completion
  const pollForPdf = useCallback(async () => {
    let attempts = 0;
    const maxAttempts = 60; // Poll for up to 2 minutes (2 seconds * 60)

    setIsPolling(true);

    const poll = async () => {
      try {
        const result = await refetch();
        const updatedProposal = result.data?.getProposal;

        if (updatedProposal?.proposalPdf && updatedProposal?.pdfGeneratedAt) {
          const updatedAt = new Date(updatedProposal.updatedAt);
          const pdfGeneratedAt = new Date(updatedProposal.pdfGeneratedAt);

          if (pdfGeneratedAt >= updatedAt) {
            // PDF is ready, download it
            if (toastKey) {
              close(toastKey);
              setToastKey(null);
            }
            downloadPdfFile(updatedProposal.proposalPdf);
            show({
              message: "PDF downloaded successfully!",
              options: { variant: "success" },
            });
            setIsPolling(false);
            setIsDownloading(false);
            return;
          }
        }

        attempts++;
        if (attempts >= maxAttempts) {
          if (toastKey) {
            close(toastKey);
            setToastKey(null);
          }
          show({
            message: "PDF generation is taking longer than expected. Please try again later.",
            options: { variant: "warning", autoHideDuration: 3000  },
          });
          setIsPolling(false);
          setIsDownloading(false);
          return;
        }

        // Continue polling
        setTimeout(poll, 2000); // Poll every 2 seconds
      } catch (error) {
        console.error("Error polling for PDF:", error);
        if (toastKey) {
          close(toastKey);
          setToastKey(null);
        }
        show({
          message: "Error checking PDF status. Please try again.",
          options: { variant: "error", autoHideDuration: 3000  },
        });
        setIsPolling(false);
        setIsDownloading(false);
      }
    };

    poll();
  }, [refetch, show, downloadPdfFile, close, toastKey]);

  const handleDownload = async () => {
    if (isDisabled) return;

    // Check if PDF is already available and up to date
    if (isPdfReady()) {
      downloadPdfFile(proposal.proposalPdf!);
      show({
        message: "PDF downloaded successfully!",
        options: { variant: "success", autoHideDuration: 5000  },
      });
      return;
    }

    setIsDownloading(true);

    try {
      const key = show({
        message: "Generating PDF... Please wait.",
        options: { variant: "info"},
      });
      setToastKey(key);

      // Trigger PDF generation
      await downloadProposal({
        variables: {
          proposalId: proposal.id,
          layout: (proposal.layout as ProposalLayout) || ProposalLayout.SIMPLE,
          removeUnitPriceAndQuantity: proposal.removeUnitPriceAndQuantity || false,
          removeIndividualLineItemPricing: proposal.removeIndividualLineItemPricing || false,
          attachments: [],
        },
      });

      // Start polling for completion
      pollForPdf();
    } catch (error) {
      console.error("Error generating PDF:", error);
      handleJobSizeError(error);
    }
  };

  const getTooltipTitle = () => {
    if (isPolling) return "Generating PDF... Please wait.";
    if (isDownloading) return "Preparing PDF...";
    if (isPdfReady()) return "Download PDF (ready)";
    return "Download Proposal";
  };

  const acceptFn = useCallback(async () => {
    try {
      const key = show({
        message: "Generating PDF... Please wait.",
        options: { variant: "info" },
      });
      setToastKey(key);

      await downloadProposal({
        variables: {
          proposalId: proposal.id,
          layout: (proposal.layout as ProposalLayout) || ProposalLayout.SIMPLE,
          removeUnitPriceAndQuantity: proposal.removeUnitPriceAndQuantity || false,
          removeIndividualLineItemPricing: proposal.removeIndividualLineItemPricing || false,
          attachments: [],
          forceSend: true,
        },
      });

      pollForPdf();
      return true;
    } catch (err) {
      show({
        message: "Unable to proceed. Please try again later or contact Support.",
        options: { variant: "error" },
      });
      return false;
    }
  }, [
    show,
    downloadProposal,
    proposal.id,
    proposal.layout,
    proposal.removeUnitPriceAndQuantity,
    proposal.removeIndividualLineItemPricing,
    pollForPdf,
  ]);

  const cancelFn = useCallback(async () => {
    return true;
  }, []);

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  return (
    <>
      <Tooltip title={getTooltipTitle()}>
        <IconButton disabled={isDisabled} onClick={handleDownload}>
          <img
            src={DownloadIcon}
            alt="Download Proposal"
            style={{
              filter: isDisabled
                ? "invert(70%) hue-rotate(120deg)"
                : "invert(0%) hue-rotate(120deg)",
              opacity: isDownloading || isPolling ? 0.7 : 1,
            }}
          />
        </IconButton>
      </Tooltip>
      <ActionConfirmDialog
        open={open}
        setOpen={setOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Confirmation"
        content={
          error?.replace("Please input a job size.", "Proceed?") ||
          "Are you sure you want to proceed?"
        }
      />
    </>
  );
}
